import { Button, Collapse } from '@blueprintjs/core';
import {
  mdiChevronDown,
  mdiChevronRight,
  mdiInformation,
  mdiMinus,
  mdiPlus,
  mdiScatterPlot,
  mdiVectorLine,
  mdiVectorSquare,
} from '@mdi/js';
import Icon from '@mdi/react';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import { IconSizeNew } from 'uf/ui/base/icon';

import { parseFullPath } from 'uf/base/dataset';
import {
  LayerReference,
  ProjectAvailableLayerItem,
  useAddToLayerListMutation,
  useGetProjectAvailableLayersQuery,
  useRemoveFromLayerListMutation,
} from 'uf-api-rtk/store/Api';
import getCategoryLabel from 'uf/projects/layerCategories';
import { Tooltip2 } from '@blueprintjs/popover2';
import { useFavoriteLayers } from 'uf/ui/layers/hooks';
import styles from 'uf/ui/layers/LayerManager/LayerManager.module.css';
import { useDispatch, useSelector } from 'react-redux';
import {
  categoryCollapsed,
  categoryExpanded,
  selectLayer,
  selectLayerManager,
} from 'uf/projects/layerManagerSlice';
import { getActiveProjectId } from 'uf/app/selectors';

export const getIconByGeoType = geoType => {
  switch (geoType?.toLowerCase()) {
    case 'linestring':
    case 'multilinestring':
      return mdiVectorLine;
    case 'point':
    case 'multipoint':
      return mdiScatterPlot;
    case 'polygon':
    case 'multipolygon':
      return mdiVectorSquare;
    case 'geometrycollection':
      return null;
    default:
      return null;
  }
};

const useCategorizedLayerList = projectId => {
  const { namespace, key } = parseFullPath(projectId);
  const { favoriteLayers } = useFavoriteLayers();
  const { data } = useGetProjectAvailableLayersQuery(
    {
      namespace,
      key,
    },
    { skip: !namespace || !key || !projectId },
  );
  const layers = _.sortBy(data?.reference, ({ layer }) => layer?.details?.name);
  const favorites = useMemo(() => {
    if (!favoriteLayers.length) {
      return [];
    }
    return _.sortBy(
      _.filter(layers, ({ layer }) => {
        const favoriteIds = _.map(
          favoriteLayers,
          favorite => favorite.full_path,
        );
        return favoriteIds.includes(layer?.full_path);
      }),
      ({ layer }) => layer?.details?.name,
    );
  }, [favoriteLayers, layers]);
  const categories = useMemo(
    () =>
      _.sortBy(
        _.map(
          _.groupBy(layers, ({ layer }) => {
            // @ts-ignore
            return layer?.details?.display_hints?.layer_category;
          }),
          (layerReferences, categoryKey) => {
            return {
              categoryKey,
              categoryLabel: getCategoryLabel(categoryKey),
              layerReferences,
            };
          },
        ),
        category => category.categoryLabel,
      ),
    [layers],
  );
  return {
    categories,
    favorites,
  };
};

export default function LayerManagerList() {
  const dispatch = useDispatch();
  const projectId = useSelector(getActiveProjectId);
  const { selectedLayerId: activeLayerId, isExpandedByCategory } =
    useSelector(selectLayerManager);
  const { namespace, key } = parseFullPath(projectId);
  const { categories, favorites } = useCategorizedLayerList(projectId);
  const [addToLayerList] = useAddToLayerListMutation();
  const [removeFromLayerList] = useRemoveFromLayerListMutation();

  const handleToggleLayer =
    ({ layerPath, selected }) =>
    async e => {
      e.stopPropagation();
      if (selected) {
        await removeFromLayerList({ layerPath, namespace, key });
      } else {
        await addToLayerList({ layerPath, namespace, key });
      }
    };

  const handleToggleCategory = categoryKey => {
    if (isExpandedByCategory[categoryKey]) {
      dispatch(categoryCollapsed(categoryKey));
    } else {
      dispatch(categoryExpanded(categoryKey));
    }
  };

  return (
    <ul
      style={{
        width: '100%',
        maxHeight: 'calc(70vh - 102px)',
        overflowY: 'scroll',
        border: '1px solid #D9E2ED',
      }}>
      {!!favorites.length && (
        <li
          key="favorites"
          style={{
            backgroundColor: '#F4F7FB',
          }}>
          <Button
            data-testid="category-list-item"
            style={{ height: 32, borderBottom: '1px solid #D9E2ED' }}
            alignText="left"
            onClick={() => handleToggleCategory('favorites')}
            minimal
            small
            fill
            icon={
              <Icon
                className="tw-text-blue-500"
                path={
                  isExpandedByCategory['favorites']
                    ? mdiChevronDown
                    : mdiChevronRight
                }
                size={IconSizeNew.SMALL}
              />
            }
            rightIcon={
              <Tooltip2
                content={
                  <p className="tw-w-48">
                    Favorite layers are automatically added to all new projects
                  </p>
                }>
                <Icon
                  className="tw-text-blueGray-600"
                  path={mdiInformation}
                  size={IconSizeNew.EXTRA_SMALL}
                />
              </Tooltip2>
            }>
            {`Favorite Layers (${favorites.length})`}
          </Button>
          <Collapse isOpen={isExpandedByCategory['favorites']}>
            <LayerList
              activeLayerId={activeLayerId}
              layerReferences={favorites}
              onToggle={handleToggleLayer}
            />
          </Collapse>
        </li>
      )}
      {Object.values(categories)
        .filter(({ layerReferences }) => layerReferences.length)
        .map(({ categoryKey, categoryLabel, layerReferences }) => {
          const isExpanded = isExpandedByCategory[categoryKey];
          return (
            <li
              key={categoryKey}
              style={{
                backgroundColor: '#F4F7FB',
              }}>
              <Button
                data-testid="category-list-item"
                style={{ height: 32, borderBottom: '1px solid #D9E2ED' }}
                alignText="left"
                onClick={() => handleToggleCategory(categoryKey)}
                minimal
                small
                fill
                icon={
                  <Icon
                    className="tw-text-blue-500"
                    path={isExpanded ? mdiChevronDown : mdiChevronRight}
                    size={IconSizeNew.SMALL}
                  />
                }>
                {`${categoryLabel} (${layerReferences.length})`}
              </Button>
              <Collapse isOpen={isExpanded}>
                <LayerList
                  activeLayerId={activeLayerId}
                  layerReferences={layerReferences}
                  onToggle={handleToggleLayer}
                />
              </Collapse>
            </li>
          );
        })}
    </ul>
  );
}

interface Props {
  activeLayerId: string | null;
  layerReferences: ProjectAvailableLayerItem[];
  onToggle: ({
    layerPath,
    selected,
  }: {
    layerPath: any;
    selected: any;
  }) => (e: any) => Promise<void>;
}

export function LayerList({ layerReferences, onToggle }: Props) {
  const itemRefs = useRef({});
  const dispatch = useDispatch();
  const { selectedLayerId: activeLayerId } = useSelector(selectLayerManager);
  useEffect(() => {
    if (activeLayerId && itemRefs.current[activeLayerId]) {
      itemRefs.current[activeLayerId].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [activeLayerId]);
  return (
    <ul style={{ backgroundColor: '#FFF' }}>
      {/* @ts-ignore */}
      {layerReferences.map(
        ({ layer, selected }: { layer: LayerReference; selected: boolean }) => {
          const {
            key: layerKey,
            full_path: fullPath,
            details: { display_hints: displayHints, name },
          } = layer;
          const active = activeLayerId === fullPath;
          const icon = getIconByGeoType(
            // @ts-ignore
            displayHints?.uf_geometry_type,
          );
          return (
            <li
              ref={el => (itemRefs.current[fullPath] = el)}
              key={layerKey}
              style={{ borderBottom: '1px solid #D9E2ED' }}>
              <Button
                data-testid="category-layer-list-item"
                alignText="left"
                onClick={() => dispatch(selectLayer(fullPath))}
                style={{
                  backgroundColor: active ? '#E3F2FD' : 'initial',
                }}
                className={styles.layerListItem}
                minimal
                fill
                icon={
                  <Icon
                    color={active ? '#000' : '#92A6BE'}
                    path={icon}
                    size={IconSizeNew.SMALL}
                  />
                }
                rightIcon={
                  <Tooltip2 content={selected ? 'Remove Layer' : 'Add Layer'}>
                    <Button
                      data-testid="toggle-layer-button"
                      style={{ borderRadius: '100%' }}
                      minimal
                      small
                      onClick={onToggle({ layerPath: fullPath, selected })}
                      icon={
                        <Icon size={1} path={selected ? mdiMinus : mdiPlus} />
                      }
                    />
                  </Tooltip2>
                }>
                {name}
              </Button>
            </li>
          );
        },
      )}
    </ul>
  );
}
